import { useEffect } from "react";
import { useGlobalContext } from "@components/contextProvider/ContextProvider";
import axios from "axios";
import UserDao from "@dao/UserDao";
import { UserDetails } from "@/types";

const inFlightRequests = new Map<string, Promise<{ data: unknown } | null>>();

/**
 * Custom hook to manage User Details — follows VOE's useUserDetails pattern.
 * 1. Hydrates from sessionStorage on mount.
 * 2. If missing, fetches from the API when token + username are available.
 * 3. Updates GlobalContext and sessionStorage.
 */
const useUserDetails = (): UserDetails | null => {
    const { dispatch, state } = useGlobalContext();
    const { userDetails, username } = state ?? {};

    /**
     * Manage User Details: Hydrate from session storage or fetch from API.
     */
    useEffect((): (() => void) => {
        const controller = new AbortController();
        const { signal } = controller;
        let isActive = true;

        const hydrateFromStorage = (): boolean => {
            try {
                const stored = sessionStorage.getItem("user_details");
                if (stored) {
                    const parsed = JSON.parse(stored) as UserDetails;
                    if (isActive) {
                        dispatch({ type: "UserDetails", userDetails: parsed });
                    }
                    return true;
                }
            } catch (error) {
                console.error("Failed to hydrate user details from session storage", error);
                sessionStorage.removeItem("user_details");
            }
            return false;
        };

        const performApiFetch = async (): Promise<void> => {
            if (!username || inFlightRequests.has(username)) return;

            const requestPromise = UserDao({ action: "userDetailsRead", username, signal });
            if (!requestPromise) return;

            inFlightRequests.set(username, requestPromise);

            try {
                const response = await requestPromise;

                if (!isActive || signal.aborted) return;

                const payload = (response?.data as { payload: unknown } | undefined)?.payload;

                if (payload) {
                    sessionStorage.setItem("user_details", JSON.stringify(payload));
                    dispatch({ type: "UserDetails", userDetails: payload as UserDetails });
                } else {
                    dispatch({ type: "UserDetailsError", userDetailsError: true });
                }
            } catch (error) {
                if (!axios.isCancel(error) && isActive) {
                    console.error("Error fetching user details:", error);
                    sessionStorage.removeItem("user_details");
                    dispatch({ type: "UserDetailsError", userDetailsError: true });
                }
            } finally {
                inFlightRequests.delete(username);
            }
        };

        const run = async (): Promise<void> => {
            if (userDetails) return;
            if (hydrateFromStorage()) return;
            await performApiFetch();
        };

        void run();

        return (): void => {
            isActive = false;
            controller.abort();
        };
    }, [username, userDetails, dispatch]);

    const isValid = userDetails && typeof userDetails === "object" && Object.keys(userDetails).length > 0;
    return isValid ? userDetails : null;
};

export default useUserDetails;
